import React, { useEffect, useState } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { SelectInputMulti } from '@uhg-abyss/web/ui/SelectInputMulti';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { LagoonContext } from '@src/AppContext';

const Wrapper = styled('div', {
  '.abyss-descriptors-error-message': {
    fontSize: '13px ',
    fontWeight: '400',
  },
});
const FlexDisplay = styled('div', {
  display: 'flex',
  gap: '20px 4%',
  alignItems: 'baseline',
  flexWrap: 'wrap',

  '@screen < $sm': {
    gap: '20px 0',
  },
});

const FlexItem = styled('div', {
  flexBasis: '100%',
  '@screen < $sm': {
    flexBasis: '100%',
  },
});
const Title = styled('h2', {
  color: 'rgb(0, 38, 119)',
  textAlign: 'left',
  fontSize: '25.63px !important',
  fontWeight: '700',
  minHeight: '32px',
  lineHeight: '32px',
  marginBottom: '5px',
});

export const OrgStepTwo = () => {
  const form = useForm();
  const [swaggerOptions, setSwaggerOptions] = useState([]);
  const lagoonContext = React.useContext(LagoonContext);
  const apisData = lagoonContext.getTable('outlook-integrations-apis').getRow();
  const orgType = form.getValues('orgInfoPartOne.orgType');

  const updateSwaggerOptions = (options) => {
    function getRaw(s) {
      return s.replace(/[&/\\#,+()$~%!.„'":*‚^_¤?<>|@ª{«»§}©®™ ]/g, '').trim();
    }
    const sortedOptions = options?.sort((a, b) => {
      return getRaw(a.section.toLowerCase()) < getRaw(b.section.toLowerCase())
        ? -1
        : 1;
    });
    setSwaggerOptions(sortedOptions);
  };

  const subCategories = (title, endpointList) => {
    const finalList = [];
    const list = endpointList.split(', ');
    list.forEach((item) => {
      const obj = {
        value: `${item}`,
        label: `${item}`,
      };
      finalList.push(obj);
    });
    return finalList;
  };
  const obj = apisData?.map((item) => {
    return {
      section: item.api,
      items: subCategories(item.api, item.endpoints),
    };
  });
  useEffect(() => {
    updateSwaggerOptions(obj);
  }, []);

  return (
    <Wrapper>
      <Title>Tell us more about your organization</Title>
      <FlexDisplay>
        {/* if orgType is provider */}
        {orgType === 'Provider' && (
          <FlexItem>
            <SelectInput
              id="emrVendor"
              label="Which EMR system does your team use?"
              model="orgInfoPartTwo.emrVendor"
              placeholder="Choose an option"
              isClearable
              isSearchable
              options={[
                { label: 'Athena', value: 'Athena' },
                {
                  label: 'Centricity',
                  value: 'Centricity',
                },
                { label: 'Cerner', value: 'Cerner' },
                { label: 'ECW', value: 'ECW' },
                { label: 'Epic', value: 'Epic' },
                {
                  label: 'NextGen',
                  value: 'NextGen',
                },
                { label: 'VER', value: 'VER' },
                { label: 'Other', value: 'Other' },
              ]}
              validators={{ required: true }}
              css={{
                'abyss-select-input-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
                'abyss-select-input-input-container': {
                  width: '48%',
                },
              }}
            />
          </FlexItem>
        )}
        {/* if org type is rcm */}
        {orgType === 'Revenue Cycle Management (RCM)' && (
          <FlexItem>
            <SelectInputMulti
              id="rcmSupportedServices"
              label="What services do you support within your organization?"
              model="orgInfoPartTwo.rcmSupportedServices"
              placeholder="Choose an option"
              isClearable
              isSearchable
              options={[
                {
                  label: 'Claims Status and Payment',
                  value: 'Claims Status and Payment',
                },
                {
                  label: 'Claims Submission',
                  value: 'Claims Submission',
                },
                {
                  label: 'Claims Appeals/Reconsiderations',
                  value: 'Claims Appeals/Reconsiderations',
                },
                { label: 'Documents', value: 'Documents' },
                {
                  label: 'Prior Authorizations and Notifications ',
                  value: 'Prior Authorizations and Notifications ',
                },
                {
                  label: 'Eligibility and Benefits',
                  value: 'Eligibility and Benefits',
                },
                {
                  label: 'Provider Demographics',
                  value: 'Provider Demographics',
                },
                { label: 'Referrals', value: 'Referrals' },
                { label: 'Dental', value: 'Dental' },
                { label: 'Other', value: 'Other' },
                { label: 'N/A', value: 'N/A' },
              ]}
              validators={{ required: true }}
              css={{
                'abyss-select-input-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
                'abyss-select-input-multi-input': {
                  width: '48%',
                },
              }}
            />
          </FlexItem>
        )}
        <FlexItem>
          <SelectInputMulti
            id="selectedAPIs"
            label="What API solution(s) are you interested in?"
            model="orgInfoPartTwo.selectedAPIs"
            placeholder="Choose an option"
            isClearable
            isSearchable
            options={swaggerOptions}
            validators={{ required: true }}
            maxListHeight="290px"
            css={{
              'abyss-select-input-multi-label': {
                position: 'relative',
                fontSize: '15px',
              },
              'abyss-select-input-multi-input': {
                width: '48%',
              },
            }}
          />
        </FlexItem>
        <FlexItem>
          <RadioGroup
            label="Do you have IT resources to help integrate our API services into your systems?"
            model="orgInfoPartTwo.isItResourceAvailable"
            display="row"
            validators={{ required: true }}
            css={{
              'abyss-radio-group-label': {
                position: 'relative',
                fontSize: '15px',
              },
            }}
          >
            <RadioGroup.Radio label="Yes" value="Yes" />
            <RadioGroup.Radio label="No" value="No" />
          </RadioGroup>
        </FlexItem>
        {form.getValues('orgInfoPartTwo.isItResourceAvailable') === 'Yes' && (
          <FlexItem>
            <RadioGroup
              id="itResourceType"
              label="Are your IT resources internal or outsourced?"
              model="orgInfoPartTwo.itResourceType"
              display="row"
              validators={{ required: true }}
              css={{
                'abyss-radio-group-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
              }}
            >
              <RadioGroup.Radio label="Internal" value="Internal" />
              <RadioGroup.Radio label="Outsourced" value="Outsourced" />
            </RadioGroup>
          </FlexItem>
        )}
        {form.getValues('orgInfoPartTwo.itResourceType') === 'Outsourced' &&
          form.getValues('orgInfoPartTwo.isItResourceAvailable') === 'Yes' && (
            <FlexItem>
              <TextInput
                id="itCompanyName"
                label="If outsourced, Which company do you use?"
                type="text"
                model="orgInfoPartTwo.itCompanyName"
                isClearable
                width="48%"
                css={{
                  'abyss-text-input-label': {
                    position: 'relative',
                    fontSize: '15px',
                  },
                }}
              />
            </FlexItem>
          )}
        {(orgType === 'Clearinghouse' ||
          orgType === 'Revenue Cycle Management (RCM)') && (
          <FlexItem>
            <SelectInput
              id="automations"
              label="Do you currently use any automation transactions in UHC Provider Portal?(eg., BOTS)"
              model="orgInfoPartTwo.automations"
              placeholder="Choose an option"
              isClearable
              isSearchable
              options={[
                { label: 'Yes', value: 'Yes' },
                {
                  label: 'No',
                  value: 'No',
                },
                {
                  label: "I don't know",
                  value: "I don't know",
                },
              ]}
              css={{
                'abyss-select-input-label': {
                  position: 'relative',
                  fontSize: '15px',
                },
                'abyss-select-input-input-container': {
                  width: '48%',
                },
              }}
            />
          </FlexItem>
        )}
        <FlexItem>
          <SelectInput
            id="timeline"
            label="What is your timeline for automating workflow with an API solution?"
            model="orgInfoPartTwo.timeline"
            placeholder="Choose an option"
            isClearable
            isSearchable
            options={[
              { label: 'As soon as possible', value: 'As soon as possible' },
              {
                label: 'Within 30 Days',
                value: 'Within 30 Days',
              },
              {
                label: 'Within 60 Days',
                value: 'Within 60 Days',
              },
              {
                label: 'More than 60 Days ',
                value: 'More than 60 Days ',
              },
            ]}
            validators={{ required: true }}
            css={{
              'abyss-select-input-label': {
                position: 'relative',
                fontSize: '15px',
              },
              'abyss-select-input-input-container': {
                width: '48%',
              },
            }}
          />
        </FlexItem>
      </FlexDisplay>
    </Wrapper>
  );
};
